// Libraries
import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Input, Output, Signal, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays, addMinutes, endOfDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { take } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';

// Interfaces
import { Fragment } from 'src/app/interfaces/api/fragment';
import { Metric, MetricData } from 'src/app/interfaces/api/metric';
import { Model } from 'src/app/interfaces/api/model';
import { Planning } from 'src/app/interfaces/api/planning';
import { PlanningTask } from 'src/app/interfaces/api/task';
import { defaultBuildingProcess } from 'src/app/interfaces/shared/buildingProcess';
import {
  DayProgressData,
  EnhancedBuildingProcess,
  FragmentData,
  FragmentProgress
} from 'src/app/interfaces/shared/task-info';

// Classes
import { PlanningTools } from 'src/app/classes/planning/planning-tools';
import { Tools } from 'src/app/classes/shared/tools';

// UI
import { ButtonComponent, IndicatorComponent, RangePickerComponent, SpinnerComponent } from 'ui-library';

// Components
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';

type IndicatorColor = 'primary' | 'secondary' | 'warn' | 'danger' | 'info';

@Component({
  selector: 'app-task-info',
  templateUrl: './task-info.component.html',
  styleUrls: ['./task-info.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslateModule,
    RangePickerComponent,
    IndicatorComponent,
    ButtonComponent,
    MatTooltipModule,
    SpinnerComponent
  ]
})

export class TaskInfoComponent {
  // Services
  private readonly metricService: MetricService = inject(MetricService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly planningTools: PlanningTools = inject(PlanningTools);
  public readonly dialog: MatDialog = inject(MatDialog);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly planningService: PlanningService = inject(PlanningService);
  private readonly planningStore: PlanningStore = inject(PlanningStore);
  private readonly pop: Pop = inject(Pop);
  private readonly tools: Tools = inject(Tools);
  private readonly router: Router = inject(Router);

  // Components API
  @Input() units!: any[];
  @Input() model!: Model;
  @Output('onPlanningTaskChange') onPlanningTaskChanged: EventEmitter<PlanningTask> = new EventEmitter();
  @Output('onMetricSelected') onMetricSelected: EventEmitter<Metric> = new EventEmitter();
  @Output('onClose') closeSideBar: EventEmitter<PlanningTask> = new EventEmitter();

  // Variables
  task!: PlanningTask;

  fragmentByIds!: Record<string, Fragment>;
  metrics: Metric[] = [];
  displayedMetrics: Metric[] = [];
  session: Record<string, string> = {};
  offset: number = 0;
  metricCount: number = 0;
  metricsByPlanningTask: Record<string, any[]> = {}; // { [taskId]: metrics[] }
  scrollLoading: any;
  attachments: any = [];
  taskMetrics: Metric[] = [];
  buildingProcess = { ...defaultBuildingProcess };
  taskMetricsByPlanningTask: any;
  scrollThreshold: number = 200;

  // Lazy loading on metrics/attachments
  metricDisplayOffset: number = 0;
  metricDisplayBatchSize: number = 100;
  attachmentOffset: number = 0;
  attachmentBatchSize: number = 100;
  allMetricQueries: Array<{ trackerId: string; startDate: string; endDate: string }> = [];
  metricIndex = new Map<string, Metric>();
  hasMoreAttachments: boolean = true;
  hasMoreMetrics: boolean = true;
  isLoadingAttachments: boolean = false;
  isLazyLoading: boolean = false;

  status = [
    { name: 'todo', color: '#gray' },
    { name: 'progress', color: '#orange' },
    { name: 'done', color: '#green' }
  ];

  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ['dev'], manual: ['dev', 'admin'] }
    }
  };

  readonly statusUiMap: Record<string, { name: string; icon: string; color: IndicatorColor }> = {
    done: { name: 'status.done', icon: 'check_circle', color: 'primary' },
    progress: { name: 'status.in_progress', icon: 'autorenew', color: 'secondary' },
    todo: { name: 'status.to_do', icon: 'schedule', color: 'warn' },
    late: { name: 'status.late', icon: 'error', color: 'danger' },
  };

  readonly defaultStatusUi: { icon: string; color: IndicatorColor } = {
    icon: 'help_outline',
    color: 'secondary'
  };

  // Signals
  planning: Signal<Planning> = signal<Planning>({} as Planning);
  tasks: Signal<PlanningTask[]> = signal<PlanningTask[]>([]);
  fragments: Signal<Fragment[]> = signal<Fragment[]>([]);
  currentPlanningTask: Signal<PlanningTask | null> = signal<PlanningTask | null>(null);
  currentMetric = signal<Metric | null>(null);
  loadingPlanning: Signal<boolean> = signal<boolean>(false);
  loadingFragments: Signal<boolean> = signal<boolean>(false);
  showUnlinked: Signal<boolean> = signal<boolean>(false);
  selectedMetrics: Signal<Metric[]> = signal<Metric[]>([]);
  selectedFragments: Signal<Fragment[]> = signal<Fragment[]>([]);
  selectedPlanningTasks: Signal<PlanningTask[]> = signal<PlanningTask[]>([]);
  isLoadingMetrics = signal<boolean>(false);
  isClosing = signal<boolean>(false);
  currentDate: string | null = null;
  metricDetail: WritableSignal<Metric | null> = signal<Metric | null>(null);

  constructor() {
    // 📦 Indexer tous les fragments
    effect(() => {
      this.fragmentByIds = this.planningStore.fragmentByIds()
    });

    // 📅 Mettre à jour date
    effect(() => {
      this.currentDate = this.planningStore.currentDate();
      this.updateTask()
    });

    // 🎯 Charger la tâche sélectionnée
    effect(() => {
      let task = this.planningStore.currentTask();

      if (task) {
        this.task = task;
        this.loadTaskData();
      }
    });

    // 🔒 Afficher métriques isolées uniquement
    effect(() => {
      if (this.isLazyLoading) return;

      const isolated = this.planningStore.isolatedMetrics();
      if (isolated.length) {
        this.displayedMetrics = isolated;
        this.metricDisplayOffset = isolated.length;
        this.hasMoreMetrics = false;
      }
    });

    // 🔗 Filter display metrics
    effect(() => {
      const fragments = this.planningStore.selectedFragments();
      const metrics = this.planningStore.selectedMetrics();

      // Cas 1: Fragment sélectionné → afficher liste du fragment
      if (fragments.length) {
        const ids = fragments.map(f => f.id);
        this.displayedMetrics = this.metrics.filter(m => ids.includes(m.payload['fragment']));
        this.metricDisplayOffset = this.displayedMetrics.length;
        this.hasMoreMetrics = false;
        this.metricDetail.set(null);
        return;
      }

      // Cas 2: une seule métrique → afficher détail
      if (metrics.length === 1) {
        const metric = metrics[0];
        if (!metric) return;

        this.displayedMetrics = [];

        if (metric.attachments?.length) {
          this.metricDetail.set(metric);
        } else {
          this.loadSelectedMetricAttachments(metric);
        }
        return;
      }

      // Cas 3: Défaut → afficher liste complète
      this.displayedMetrics = this.metrics.slice(0, this.metricDisplayBatchSize);
      this.metricDisplayOffset = this.displayedMetrics.length;
      this.hasMoreMetrics = this.metrics.length > this.metricDisplayBatchSize;
      this.metricDetail.set(null);
    });


    // 📊 Charger les métriques tâche
    effect(() => {
      if (this.task?.metrics) {
        this.taskMetrics = this.task.metrics;
      }
    });
  }

  ////////////////////////////
  // Angular lifecycle hook //
  ////////////////////////////


  private async loadTaskData() {
    if (!this.task) return;

    this.metrics = this.planningStore.metricsByTaskIds()[this.task.id] || [];
    this.updateTask();
    await this.getAttachments(this.metrics);
  }

  onAttachmentClick(metric: Metric) {
    if (!metric.attachments?.length) return;

    let attachment = metric.attachments[metric.attachments.length - 1];
    const attachmentCreatedDate = this.toDate(attachment?.createdDate);

    const dialogRef = this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: {
        index: metric.attachments!.length - 1,
        id: attachment?._id || attachment?.id,
        urls: metric.attachments!.map(a => a.url),
        startDate: attachment?.createdDate,
        endDate: addMinutes(attachmentCreatedDate, 1).toISOString(),
        boxes: attachment?.predictions,
        tagDisplayConfig: this.tagDisplayConfig
      }
    });

    dialogRef.afterClosed().subscribe((res: any) => {
      // if (res && res.updated) {
      //   this.getMetrics();
      // }
    });

  }
  onMetricClick(metric: Metric) {
    // this.onMetricSelected.emit(metric)
    // this.currentMetric.set(metric);
    this.planningStore.selectMetrics([metric])
    // this.getAttachment(metric)
  }

  onPickerChanged(event: { startDate: any; endDate: any }) {
    let task = this.task;

    if (!task) return;

    task.startDate = this.toIsoStringValue(event.startDate);
    task.endDate = this.toIsoStringValue(event.endDate);

    if (isAfter(this.toDate(task.startDate), this.toDate(task.endDate))) {
      task.endDate = addDays(this.toDate(task.startDate), 1).toISOString();
    }

    this.planningStore.updateTask(task);
    this.editTask();
  }

  onRealDateChanged(event: { startDate: any; endDate: any }) {
    let task = this.task;

    if (!task) return;

    task.doneDate = this.toIsoStringValue(event.endDate);
    this.planningStore.updateTask(task);
    this.editTask();
  }

  updateTask() {
    const task = this.task;

    if (!task) return;

    // ── Initialisation des accumulateurs ──────────────────────────────────────
    const CATEGORIES = ['formwork', 'concreting', 'reinforcement', 'prefabricated', 'other'] as const;
    const emptyMetric = () => ({ duration: 0, weight: 0, cost: 0 });

    const metricData: MetricData = {
      total: emptyMetric(),
      ...Object.fromEntries(CATEGORIES.map(c => [c, emptyMetric()]))
    } as MetricData;

    const fragmentData: FragmentData = {
      total: 0, volumeProgress: 0, dayProgress: 0, todo: 0, done: 0, progress: 0
    };

    const dayProgress: DayProgressData = { progress: 0, done: 0, volume: 0 };

    // Map des progressions par fragment (clé = fragment ID)
    const fragments: Record<string, FragmentProgress> = {};

    task.progress = 0;
    task.volumeProgress = 0;
    task.metrics ??= [];

    // ── Pré-calcul des bornes temporelles (évite les recréations dans la boucle) ──
    const now = this.toDate(this.currentDate);
    const startOfCurrentDay = startOfDay(now);
    const endOfCurrentDay = endOfDay(now);

    // ── Agrégation des métriques ──────────────────────────────────────────────
    for (const m of task.metrics) {
      // const m = this.planningStore.metricByIds()[metricId]
      let { fragment, duration, weight, volume, category, cost: rawCost } = m.payload;

      if (!fragment) continue;

      // Les métriques futures sont ignorées (les données sont triées par date croissante)
      const createdDate = this.toDate(m.createdDate);

      if (isAfter(createdDate, now)) break;

      const isThisDay = isAfter(createdDate, startOfCurrentDay) && isBefore(createdDate, now);

      // Accumulation durée / poids
      if (duration && category) {
        const d = parseFloat(duration as string);
        metricData[category as keyof MetricData].duration += d;
        metricData.total.duration += d;
      }

      if (weight && category) {
        const w = parseFloat(weight as string);
        metricData[category as keyof MetricData].weight += w;
        metricData.total.weight += w;
      }

      if (!volume) {
        volume = weight as number / 2300
      }

      // Calcul du coût : priorité au coût explicite, sinon poids × coût unitaire
      const unit = this.units.find(u => u.name === category);
      const cost = rawCost ?? (unit && weight ? parseFloat(String(weight)) * unit.costByUnit : 0);

      if (category) {
        metricData[category as keyof MetricData].cost += cost;
        metricData.total.cost += cost;
      }

      // Initialisation de l'entrée du fragment au premier passage
      if (!fragments[fragment]) {
        fragments[fragment] = { status: 'progress', date: m.createdDate, volumeProgress: 0, dayProgress: 0 };
      }

      // Accumulation du volume du fragment
      if (volume) {
        fragments[fragment]!.volumeProgress += parseFloat(volume as string);

        if (isThisDay) {
          dayProgress.volume += parseFloat(volume as string);
          fragments[fragment]!.dayProgress += parseFloat(volume as string);
        }
      }

      // Passage au statut "done" si ≥ 75 % du volume total atteint
      const fragmentDef = this.fragmentByIds[fragment];

      if (fragmentDef) {
        const fragmentVolume = fragmentDef.volume ?? 0;

        if (fragments[fragment]!.volumeProgress >= fragmentVolume * 0.75) {
          fragments[fragment]!.volumeProgress = fragmentVolume; // cap au volume total
          fragments[fragment]!.status = 'done';
        }
      }

      fragments[fragment]!.date = m.createdDate;
    }

    // ── Initialisation du résumé par type structurel ──────────────────────────
    const emptyBuildingEntry = () => ({
      measure: 0, progress: 0, dayProgress: 0,
      dayProgressText: '', progressText: '', measureText: '', percentText: '', duration: 0
    });

    const buildingProcess: EnhancedBuildingProcess = {
      wall: emptyBuildingEntry(),
      floor: emptyBuildingEntry(),
      column: emptyBuildingEntry(),
    };

    task.volume = 0;

    // ── Calcul des mesures totales (objectif) par type structurel ─────────────
    (task.fragments ??= []).forEach(f => {
      const fragment = this.fragmentByIds[f];

      if (!fragment) return;

      // Résolution du type de bâtiment via le modèle BIM si disponible
      if (fragment.mapping?.element) {
        fragment.buildingType = this.model
          ? this.planningTools.getFormattedBuildingType(this.model.buildingTypes ?? {}, fragment.mapping.element.buildingType ?? '')
          : 'unknown';

        const { dimension } = fragment.mapping;
        const thickness = dimension?.thickness ?? dimension?.width ?? 0.2;
        const volume = dimension?.volume ?? fragment.volume ?? 0;
        task.volume += volume;

        switch (fragment.buildingType) {
          case 'wall':
          case 'prefaWall':
            buildingProcess.wall.measure += volume / thickness / (fragment.height ?? 3);
            break;
          case 'floor':
            buildingProcess.floor.measure += volume / thickness;
            break;
          default:
            buildingProcess.column.measure++;
        }
      } else {
        // Fallback sur le type déclaré directement dans le fragment
        switch (fragment.type) {
          case 'wall':
            buildingProcess.wall.measure += (fragment.volume ?? 0) / 0.2 / (fragment.height ?? 3);
            break;
          case 'column':
            buildingProcess.column.measure++;
            break;
        }
      }
    });

    // ── Helper : calcule la contribution d'un fragment à buildingProcess ──────
    const accumulateBuildingProgress = (
      frag: Fragment,
      fp: FragmentProgress,
      buildingType: string | undefined,
      type: string | undefined,
      isThisDay: boolean
    ) => {
      const getThicknessAndAdd = (thickness: number, height = 3) => {
        buildingProcess.wall.progress += fp.volumeProgress / thickness / height;

        if (isThisDay)
          buildingProcess.wall.dayProgress += fp.dayProgress / thickness / height;
      };

      if (frag?.mapping?.element) {
        const { dimension } = frag.mapping;
        const thickness = dimension?.thickness ?? dimension?.width ?? 0.2;

        switch (buildingType) {
          case 'wall':
          case 'prefaWall':
            getThicknessAndAdd(thickness, frag?.height ?? 3);
            break;
          case 'floor':
            buildingProcess.floor.progress += fp.volumeProgress / thickness;
            if (isThisDay) buildingProcess.floor.dayProgress += fp.dayProgress / thickness;
            break;
          default:
            if (fp.status === 'done') {
              buildingProcess.column.progress++;
              if (isThisDay) buildingProcess.column.dayProgress++;
            }
        }
      } else {
        switch (type) {
          case 'wall':
            buildingProcess.wall.progress += fp.volumeProgress / 0.2 / (frag?.height ?? 3);

            if (isThisDay)
              buildingProcess.wall.dayProgress += fp.dayProgress / 0.2 / (frag?.height ?? 3);
            break;
          case 'floor':
            buildingProcess.floor.progress += fp.volumeProgress / 0.2;

            if (isThisDay)
              buildingProcess.floor.dayProgress += fp.dayProgress / 0.2;
            break;
          case 'column':
            if (fp.status === 'done') {
              buildingProcess.column.progress++;

              if (isThisDay)
                buildingProcess.column.dayProgress++;
            }
        }
      }
    };

    // ── Agrégation des progressions par fragment ──────────────────────────────
    Object.entries(fragments).forEach(([f, fp]) => {
      // Comptage statut (todo / progress / done)
      fragmentData[fp.status as keyof FragmentData]++;

      const fragmentDate = this.toDate(fp.date);
      const isThisDay = isAfter(fragmentDate, startOfCurrentDay) && isBefore(fragmentDate, endOfCurrentDay);
      const frag = this.fragmentByIds[f];

      if (!frag) return;

      // Résolution du buildingType si mapping BIM disponible
      if (frag?.mapping?.element) {
        frag.buildingType = this.model
          ? this.planningTools.getFormattedBuildingType(this.model.buildingTypes ?? {}, frag.mapping.element.buildingType ?? '')
          : 'unknown';
      }

      accumulateBuildingProgress(frag, fp, frag?.buildingType, frag?.type, isThisDay);

      if (isThisDay && fp?.status)
        dayProgress[fp.status]++;

      fragmentData.volumeProgress += fp.volumeProgress;
    });

    // ── Formatage des textes d'affichage ──────────────────────────────────────
    Object.values(buildingProcess).forEach(bp => {
      bp.progressText = bp.progress.toFixed(2);
      bp.measureText = bp.measure.toFixed(2);
      bp.percentText = bp.measure ? ((bp.progress / bp.measure) * 100).toFixed(2) : '0.00';
      bp.dayProgressText = bp.dayProgress.toFixed(2);
    });

    // ── Mise à jour finale de la tâche ────────────────────────────────────────
    fragmentData.total = task.fragments.length;
    fragmentData.todo = fragmentData.total - fragmentData.progress - fragmentData.done;

    task.progress = fragmentData.volumeProgress / task.volume;
    task.progressText = (task.progress * 100).toFixed(2);
    task.volumeProgress = fragmentData.volumeProgress;
    task.volumeProgressText = (+fragmentData.volumeProgress).toFixed(2);
    task.volumeText = (+task.volume).toFixed(2);
    task.dayProgress = dayProgress as any;
    task.fragmentData = fragmentData;
    task.metricData = metricData;
    task.buildingProcess = buildingProcess;

    dayProgress.volume = parseFloat(dayProgress.volume.toFixed(2));
    dayProgress.volumeText = dayProgress.volume.toString();

    this.task = task;
  }


  onScroll($event: any) {
    const target = $event.target as HTMLElement;
    const scrollPosition = target.scrollTop;
    const elementHeight = target.offsetHeight;
    const scrollBottom = target.scrollHeight - (scrollPosition + elementHeight);

    // If we are bottom of the scroll, charge more attachments
    if (scrollBottom <= this.scrollThreshold && (this.hasMoreMetrics || this.hasMoreAttachments) && !this.isLoadingAttachments) {
      this.loadMoreMetricsAndAttachments();
    }
  }

  hasMetricsToLoad(index: number) {
    if (!this.metricCount)
      return false;

    return this.offset < this.metricCount;
  }

  editTask() {
    let task = this.task;

    if (!task) return;

    let query = {
      id: task.id,
      planning: task.planning,
      text: task.text,
      number: task.number,
      duration: task.duration?.toString(),
      progress: task.progress?.toString(),
      startDate: task.startDate,
      endDate: task.endDate,
      doneDate: task.doneDate,
      cost: task.cost,
      volume: task.volume,
      fragments: task.fragments,
      fragmentDbIds: task.fragmentDbIds
    }

    this.planningService.updateTaskV2(query).subscribe((data: any) => {
      if (data.action && data.action.includes("updated")) {
        this.pop.toast(this.translate.instant("planning.task_updated"), "success"); // toast method doesn't exist
      }
    });
  }

  deleteTask() {
    // this.tools.confirm(this.translate.instant('planning.task_deleted_confirm'), (confirm: any) => { // confirm method doesn't exist
    if (window.confirm(this.translate.instant('planning.task_deleted_confirm'))) {
      // this.rebuildFragment = true;
      this.planningService.removeTask(this.task!.id).pipe(take(1)).subscribe({
        next: () => {
          // this.rebuildFragment = false;
          this.onPlanningTaskChanged.emit(undefined);
          this.close()
        },
        error: () => {
          // this.rebuildFragment = false;
        }
      })
    }
  }

  // loadMetricsForPlanningTask() {
  //   let task = this.task;

  //   if (!task || this.scrollLoading) return;

  //   this.scrollLoading = true;

  //   this.metricService.getMetricsCount({ name: "Planning", "taskId": task.id }).pipe(take(1)).subscribe(result => {
  //     this.metricCount = result;
  //   });

  //   let query: any = { name: "Planning", "taskId": task.id, limit: 100, offset: this.offset, sort: "desc" };

  //   let taskId = task.id;

  //   if (!this.taskMetricsByPlanningTask[taskId])
  //     this.taskMetricsByPlanningTask[taskId] = [];

  //   this.metricService.getMetrics(query).subscribe((result: Metric[]) => {
  //     if (this.offset == 0) {
  //       task.metrics = [];
  //     }

  //     this.scrollLoading = false;
  //     let metrics = result.filter(m => m.payload.startDate && m.payload.endDate);
  //     let taskMetrics: Metric[] = [];

  //     let metricData: MetricData = task.metricData || {
  //       total: { duration: 0, weight: 0, cost: 0 },
  //       formwork: { duration: 0, weight: 0, cost: 0 },
  //       concreting: { duration: 0, weight: 0, cost: 0 },
  //       reinforcement: { duration: 0, weight: 0, cost: 0 },
  //       prefabricated: { duration: 0, weight: 0, cost: 0 },
  //       other: { duration: 0, weight: 0, cost: 0 }
  //     }

  //     metrics.forEach(m => {
  //       if (m.payload.startDate && m.payload.endDate) {
  //         let cat = this.getCategory((m.payload['title'] as string) || '')

  //         if (!m.payload['weight'] && m.payload['volume']) {
  //           m.payload['weight'] = parseFloat(m.payload['volume'] as string) * 2300
  //         }

  //         if (m.payload['duration']) {
  //           metricData[cat as keyof MetricData].duration += parseFloat(m.payload['duration'] as string);
  //           metricData.total.duration += parseFloat(m.payload['duration'] as string);
  //         }

  //         if (m.payload['weight']) {
  //           metricData[cat as keyof MetricData].weight += parseFloat(m.payload['weight'] as string);
  //           metricData.total.weight += parseFloat(m.payload['weight'] as string);
  //         }

  //         let unit = this.units.find(u => u.name == cat);
  //         let cost = m.payload['cost'] || 0;

  //         if (unit && !m.payload['cost']) {
  //           cost = parseFloat(m.payload['weight'] as string) * unit.costByUnit;
  //         }

  //         metricData[cat as keyof MetricData].cost += cost;
  //         metricData.total.cost += cost;
  //         let volume: number = 0;

  //         if (m.payload['volume']) {
  //           volume = parseFloat(m.payload['volume'] as string);
  //         } else if (m.payload['weight']) {
  //           volume = parseFloat(m.payload['weight'] as string) / 2300;
  //         }

  //         // let taskMetric: Metric = {
  //         //   color: m.payload['color'],
  //         //   label: m.payload['title'],
  //         //   startDate: m.payload['startDate'],
  //         //   endDate: m.payload['endDate'],
  //         //   volume,
  //         //   volumeText: volume.toFixed(2),
  //         //   weight: parseFloat(m.payload['weight'] as string || '0'),
  //         //   duration: m.payload['duration'],
  //         //   title: m.payload['prediction'],
  //         //   fragment: undefined,
  //         //   id: m.id,
  //         //   cost: cost,
  //         //   left: 0,
  //         //   width: 0,
  //         //   tracker: m.tracker,
  //         //   trackerName: m.trackerName,
  //         //   prediction: m.payload['prediction'],
  //         //   attachments: []
  //         // }
  //         // if (!task.metrics) task.metrics = [];
  //         // task.metrics.push(taskMetric);
  //         // taskMetrics.push(taskMetric);
  //       }
  //       task.metricData = metricData;
  //     })

  //     if (task.metrics?.length) {
  //       this.taskMetrics = task.metrics as Metric[];
  //       this.getAttachments(taskMetrics);
  //     }

  //     if (this.taskMetricsByPlanningTask[taskId]) {
  //       this.taskMetricsByPlanningTask[taskId] = this.taskMetricsByPlanningTask[taskId].concat(metrics);
  //     }

  //     this.offset += result.length;
  //   })
  // }

  async getAttachments(metrics: Metric[]) {
    if (!metrics.length) return;

    this.isLazyLoading = true;

    // Reset
    this.attachmentOffset = 0;
    this.metricDisplayOffset = 0;
    this.hasMoreAttachments = true;
    this.hasMoreMetrics = true;
    this.isLoadingAttachments = false;
    this.displayedMetrics = [];

    metrics.forEach(m => {
      m.attachments = undefined as any;
    });

    // Prepare queries for attachments
    this.allMetricQueries = metrics
      .filter(m => m.payload?.startDate && m.payload?.endDate)
      .map(m => {
        const trackerId = typeof m.tracker === 'string' ? m.tracker : m.tracker?.id || '';
        return {
          trackerId,
          startDate: this.toDate(m.payload.startDate).toISOString(),
          endDate: this.toDate(m.payload.endDate).toISOString()
        };
      })
      .filter(q => q.trackerId);

    this.metricIndex.clear();

    // Create an index to quickly assign attachments
    metrics.forEach(m => {
      if (m.payload?.startDate && m.payload?.endDate) {
        const trackerId = typeof m.tracker === 'string' ? m.tracker : m.tracker?.id || '';
        const key = `${trackerId}-${this.toDate(m.payload.startDate).toISOString()}-${this.toDate(m.payload.endDate).toISOString()}`;
        this.metricIndex.set(key, m);
        m.attachments = undefined as any;
      }
    });

    // Show first 100 metrics/attachments
    await this.loadMoreMetricsAndAttachments();
    this.isLazyLoading = false;
  }

  async loadMoreMetricsAndAttachments() {
    if (this.isLoadingAttachments || (!this.hasMoreMetrics && !this.hasMoreAttachments)) {
      return;
    }

    this.isLoadingAttachments = true;
    this.scrollLoading = true;

    try {
      if (this.hasMoreMetrics) {
        const nextBatch = this.metrics.slice(
          this.metricDisplayOffset,
          this.metricDisplayOffset + this.metricDisplayBatchSize
        );

        this.displayedMetrics = [...this.displayedMetrics, ...nextBatch];
        this.metricDisplayOffset += nextBatch.length;
        this.hasMoreMetrics = this.metricDisplayOffset < this.metrics.length;
      }

      // Load attachments in batches of 100
      if (this.attachmentOffset < this.allMetricQueries.length) {
        const response = await firstValueFrom(
          this.attachmentService.getAttachmentsByMetricsGrouped({
            metricQueries: this.allMetricQueries,
            offset: this.attachmentOffset,
            limit: this.attachmentBatchSize
          })
        );

        if (response?.attachments) {
          let assignedCount: number = 0;

          response.attachments.forEach((att: any) => {
            const metric = this.metricIndex.get(att.metricKey);

            if (metric) {
              if (!metric.attachments) {
                metric.attachments = [];
              }

              metric.attachments.push(att);
              assignedCount++;
            }
          });

          this.displayedMetrics.forEach(metric => {
            if (metric.attachments === undefined) {
              metric.attachments = [];
            }
          });

          this.attachmentOffset += this.attachmentBatchSize;
          this.hasMoreAttachments = response.hasMore;
        }
      } else {
        this.hasMoreAttachments = false;
      }
    } catch (error) {
      console.error(`❌ Error loading:`, error);
    } finally {
      this.isLoadingAttachments = false;
      this.scrollLoading = false;
    }
  }

  private async loadSelectedMetricAttachments(metric: Metric): Promise<void> {
    if (!metric.payload?.startDate || !metric.payload?.endDate) return;

    const trackerId = typeof metric.tracker === 'string' ? metric.tracker : metric.tracker?.id || '';

    return new Promise<void>((resolve, reject) => {
      try {
        metric.attachments = [];
        this.attachmentService.getAttachments({
          tracker: trackerId,
          startDate: this.toDate(metric.payload.startDate).toISOString(),
          endDate: this.toDate(metric.payload.endDate).toISOString(),
          limit: 10,
          offset: 0
        }).pipe(take(1)).subscribe({
          next: (att) => {
            metric.attachments = att;
            this.metricDetail.set(metric);
            resolve();
          },
          error: (err) => {
            console.error('Error loading metric attachments:', err);
            reject(err);
          }
        });
      } catch (err) {
        console.error(err);
        reject(err);
      }
    });
  }

  getCategory(title: string) {
    title = title.toLocaleLowerCase()
    let category = 'other'

    if (title == 'coulage' || title == 'benne à béton') {
      category = 'concreting';
    } else if (title.includes('armature')) {
      category = 'reinforcement';
      // } else if (title.includes('banche') ) {
    } else if (title == 'banche') {
      category = 'formwork';
    } else if (title.includes('préfabriqué') || title.includes('prédalle')) {
      category = 'prefabricated';
    }

    return category;
  }

  private toDate(value: unknown): Date {
    if (value instanceof Date) {
      return value;
    }

    if (value && typeof value === 'object' && 'toISOString' in value && typeof value.toISOString === 'function') {
      return new Date(value.toISOString());
    }

    return new Date(String(value));
  }

  private toIsoStringValue(value: unknown): string {
    if (value && typeof value === 'object' && 'toISOString' in value && typeof value.toISOString === 'function') {
      return value.toISOString();
    }

    return value ? String(value) : '';
  }

  hasProgressValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    const numericValue = typeof value === 'number' ? value : Number.parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(numericValue) && numericValue > 0;
  }

  parseDuration(value: string | number | undefined): string {
    return this.tools.formatDuration(value);
  }

  deselectMetric() {
    this.metricDetail.set(null);
    this.planningStore.selectMetrics([]);
  }

  redirectToTimeline(event: Event, metric: Metric) {
    event.stopPropagation();

    if (!metric.createdDate) return;
    const params: any = {
      mode: 'timeline',
      startDate: this.toDate(metric.createdDate).toISOString(),
      target: metric.id
    }
    // this.router.navigate(['/project/report'], {
    //   queryParams: params
    // });

    const queryString = Object.keys(params).map(key => `${key}=${params[key]}`).join('&');
    window.open(`/project/report?${queryString}`, "_blank");
  }

  close() {
    this.isClosing.set(true);

    // Timeout for animation close
    setTimeout(() => {
      this.planningStore.setCurrentTask(null);
      this.planningStore.selectMetrics([]);
    }, 200);
  }

  trackMetric(index: number, metric: Metric): string {
    return metric.id || index.toString();
  }
}
